import { useState, useEffect } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useGetCurrentPlayer, useGetCurrentPlayerDisplayName, usePlayerActions } from 'src/store/player'
import { useGetPlayerChannel } from 'src/store/socket'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import { useDisplayNameValidation } from './useDisplayNameValidation'

interface ToastMessage {
  message: string
  type: 'error' | 'success' | 'warning'
}

export function useDisplayNameHandler() {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const currentPlayer = useGetCurrentPlayer()
  const currentDisplayName = useGetCurrentPlayerDisplayName()
  const playerChannel = useGetPlayerChannel()
  const playerActions = usePlayerActions()
  const validateDisplayName = useDisplayNameValidation()

  const [isLoading, setIsLoading] = useState(false)
  const [displayName, setDisplayName] = useState(currentDisplayName || '')
  const [toastMessage, setToastMessage] = useState<ToastMessage | undefined>()
  const [validationError, setValidationError] = useState<string | undefined>()

  // Validate on each change
  useEffect(() => {
    if (displayName) {
      const error = validateDisplayName(displayName)
      setValidationError(error || undefined)
    } else {
      setValidationError(undefined)
    }
  }, [displayName, validateDisplayName])

  useEffect(() => {
    setDisplayName(currentDisplayName || '')
  }, [currentDisplayName])

  const submitDisplayName = () => {
    if (displayName === currentDisplayName) {
      setToastMessage({ message: t('Display name is already set'), type: 'warning' })
      return
    }

    const error = validateDisplayName(displayName)
    if (error) {
      setToastMessage({ message: error, type: 'error' })
      return
    }

    datadogLogs.logger.info('change_display_name', {
      user_action: {
        type: 'change_display_name',
        displayName: currentDisplayName,
        newDisplayName: displayName,
        playerId: isAuthenticatedPlayer(currentPlayer) ? currentPlayer.id : null
      }
    })

    setIsLoading(true)
    handleChangeDisplayName()
  }

  const handleChangeDisplayName = () => {
    if (!playerChannel) {
      console.error('Error updating display name: no player channel')
      setIsLoading(false)
      return
    }

    try {
      playerChannel
        .push('client:player:display-name:update', { display_name: displayName })
        .receive('ok', () => {
          playerActions.displayNameChanged(displayName)
          setToastMessage({ message: t('Display name changed successfully'), type: 'success' })
          setIsLoading(false)
        })
        .receive('error', (error: { error: string; details: string }) => {
          console.error('Error updating display name:', error)
          setToastMessage({ message: t('Error updating display name'), type: 'error' })
          setIsLoading(false)
        })
    } catch (error) {
      console.error('Error changing display name:', error)
      setToastMessage({ message: t('Unexpected error occurred'), type: 'error' })
      setIsLoading(false)
    }
  }

  return {
    displayName,
    setDisplayName,
    isLoading,
    submitDisplayName,
    toastMessage,
    setToastMessage,
    validationError
  }
}
